import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { formatDistanceToNow, format } from 'date-fns';
import {
  saveStageUserAssignments,
  fetchAllStageAssignments,
} from '../../../services/notificationService';

// ─── Workflow Stage Config ────────────────────────────────────────────────────

const WORKFLOW_STAGES = [
  {
    id: 'Pending Team Assignment',
    label: 'Pending Assignment',
    icon: 'UserPlus',
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    responsibleRole: 'Coordinator / Project Manager',
    roleKeys: ['project_manager', 'admin', 'super_admin'],
    exampleUser: 'Ahmed',
    description: 'A new request is pending assignment. Please assign it to the appropriate engineer/team.',
    actionLabel: 'Assign Engineer / Team',
    actionIcon: 'UserPlus',
    actionColor: 'bg-warning/10 text-warning border-warning/30 hover:bg-warning/20',
  },
  {
    id: 'Calculated',
    label: 'Calculated',
    icon: 'Calculator',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    responsibleRole: 'Estimator / Project Manager',
    roleKeys: ['project_manager', 'admin', 'super_admin'],
    exampleUser: 'Motasim',
    description: 'The request has been calculated. Please review the estimation details.',
    actionLabel: 'Review & Confirm Calculation',
    actionIcon: 'CheckSquare',
    actionColor: 'bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500/20',
  },
  {
    id: 'Pending Approval',
    label: 'Pending Approval',
    icon: 'ClipboardCheck',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    responsibleRole: 'Manager / Approver',
    roleKeys: ['approver', 'admin', 'super_admin'],
    exampleUser: 'Mohammed',
    description: 'A request is pending your approval. Please approve or reject.',
    actionLabel: 'Approve / Reject',
    actionIcon: 'ShieldCheck',
    actionColor: 'bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20',
  },
  {
    id: 'Approved',
    label: 'Approved',
    icon: 'CheckCircle',
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    responsibleRole: 'Finance Team',
    roleKeys: ['finance', 'admin', 'super_admin'],
    exampleUser: 'Fatma',
    description: 'The request has been approved. Please prepare for payment processing.',
    actionLabel: 'Prepare Payment',
    actionIcon: 'CreditCard',
    actionColor: 'bg-success/10 text-success border-success/30 hover:bg-success/20',
  },
  {
    id: 'Ready for Payment',
    label: 'Ready for Payment',
    icon: 'Banknote',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    responsibleRole: 'Finance Officer',
    roleKeys: ['finance', 'admin', 'super_admin'],
    exampleUser: 'Khalid',
    description: 'The request is ready for payment. Please confirm and execute the payment.',
    actionLabel: 'Confirm & Execute Payment',
    actionIcon: 'BadgeCheck',
    actionColor: 'bg-purple-500/10 text-purple-400 border-purple-500/30 hover:bg-purple-500/20',
  },
  {
    id: 'Paid',
    label: 'Paid',
    icon: 'Receipt',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    responsibleRole: 'Requester / Project Member',
    roleKeys: ['sales_user', 'sales_manager', 'employee'],
    exampleUser: 'Motasim',
    description: 'Your request has been successfully paid. Please check the receipt and confirmation.',
    actionLabel: 'View Receipt',
    actionIcon: 'FileText',
    actionColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); }
  catch { return '—'; }
};

const formatDate = (dateStr) => {
  try { return format(new Date(dateStr), 'dd MMM yyyy, HH:mm'); }
  catch { return '—'; }
};

// ─── User Assignment Modal ────────────────────────────────────────────────────

const UserAssignmentModal = ({ stage, allUsers, currentAssignedIds, onSave, onClose, saving }) => {
  const [selectedIds, setSelectedIds] = useState(new Set(currentAssignedIds || []));
  const [search, setSearch] = useState('');

  const filtered = allUsers?.filter(u =>
    !search ||
    u?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    u?.email?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const toggle = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(uid)) next?.delete(uid);
      else next?.add(uid);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-border ${stage?.bg}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-background/60`}>
              <Icon name={stage?.icon} size={16} className={stage?.color} />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{stage?.label}</p>
              <p className="text-xs text-muted-foreground">Assign specific users for this stage</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Info banner */}
        <div className="mx-4 mt-4 flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20">
          <Icon name="Info" size={13} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            If no users are selected, notifications will fall back to role-based assignment
            (<span className="font-mono text-xs">{stage?.roleKeys?.join(', ')}</span>).
          </p>
        </div>

        {/* Search */}
        <div className="px-4 pt-3">
          <div className="relative">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* User list */}
        <div className="overflow-y-auto max-h-64 px-4 py-3 space-y-1.5">
          {filtered?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <Icon name="Users" size={24} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No users found</p>
            </div>
          ) : (
            filtered?.map(u => {
              const isSelected = selectedIds?.has(u?.id);
              return (
                <button
                  key={u?.id}
                  onClick={() => toggle(u?.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                    isSelected
                      ? `${stage?.border} ${stage?.bg}`
                      : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isSelected ? `${stage?.bg} ${stage?.color}` : 'bg-muted text-muted-foreground'}`}>
                    {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{u?.full_name || '—'}</p>
                    <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected ? `${stage?.color} border-current bg-current/20` : 'border-border'
                  }`}>
                    {isSelected && <Icon name="Check" size={10} className={stage?.color} />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-4 border-t border-border bg-muted/10">
          <p className="text-xs text-muted-foreground">
            {selectedIds?.size} user{selectedIds?.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(stage?.id, [...selectedIds])}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${stage?.actionColor} border`}
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="Save" size={14} />
              )}
              Save Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Stage Card ───────────────────────────────────────────────────────────────

const StageCard = ({ stage, roleUsers, specificUsers, isExpanded, onToggle, onAssignUsers }) => {
  const hasSpecificUsers = specificUsers?.length > 0;

  return (
    <div className={`rounded-2xl border transition-all duration-200 overflow-hidden ${isExpanded ? `${stage?.border} shadow-sm` : 'border-border'}`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors text-left"
      >
        {/* Stage icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${stage?.bg}`}>
          <Icon name={stage?.icon} size={18} className={stage?.color} />
        </div>

        {/* Stage info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{stage?.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${stage?.bg} ${stage?.color} font-medium`}>
              {stage?.responsibleRole}
            </span>
            {hasSpecificUsers && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium flex items-center gap-1">
                <Icon name="UserCheck" size={10} />
                {specificUsers?.length} specific user{specificUsers?.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{stage?.description}</p>
        </div>

        {/* Assigned users count */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-muted-foreground">{hasSpecificUsers ? 'Specific users' : 'Role users'}</p>
            <p className={`text-sm font-bold ${(hasSpecificUsers ? specificUsers?.length : roleUsers?.length) > 0 ? stage?.color : 'text-muted-foreground'}`}>
              {hasSpecificUsers ? specificUsers?.length : (roleUsers?.length ?? '—')}
            </p>
          </div>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            className="text-muted-foreground"
          />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-border/50 p-4 space-y-4 bg-muted/10">
          {/* Notification preview */}
          <div className={`rounded-xl border ${stage?.border} p-4 ${stage?.bg}`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 p-2 rounded-lg bg-background/60`}>
                <Icon name="Bell" size={14} className={stage?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground mb-1">Notification Preview</p>
                <p className="text-sm text-foreground/80 leading-relaxed">{stage?.description}</p>
                <div className="mt-3">
                  <button className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${stage?.actionColor}`}>
                    <Icon name={stage?.actionIcon} size={12} />
                    {stage?.actionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Specific User Assignments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Specific User Assignments
              </p>
              <button
                onClick={() => onAssignUsers(stage)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${stage?.actionColor}`}
              >
                <Icon name="UserPlus" size={12} />
                {hasSpecificUsers ? 'Edit Assignment' : 'Assign Users'}
              </button>
            </div>

            {hasSpecificUsers ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {specificUsers?.map(u => (
                  <div key={u?.id} className={`flex items-center gap-3 p-3 rounded-xl border ${stage?.border} ${stage?.bg}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-background/60 ${stage?.color}`}>
                      {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{u?.full_name || '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                    </div>
                    <Icon name="UserCheck" size={14} className={stage?.color} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border">
                <Icon name="Info" size={14} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No specific users assigned. Falling back to role-based notification.
                </p>
              </div>
            )}
          </div>

          {/* Role-based fallback users */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {hasSpecificUsers ? 'Role-based Fallback Users' : 'Users who receive this notification (by role)'}
            </p>
            {roleUsers?.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border">
                <Icon name="AlertCircle" size={14} className="text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  No users assigned to roles: <span className="font-mono">{stage?.roleKeys?.join(', ')}</span>
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {roleUsers?.map(u => (
                  <div key={u?.id} className={`flex items-center gap-3 p-3 rounded-xl bg-card border border-border ${hasSpecificUsers ? 'opacity-50' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${stage?.bg} ${stage?.color}`}>
                      {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{u?.full_name || '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${stage?.bg} ${stage?.color}`}>
                        {u?.role_name || u?.role_key}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Role keys info */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Notified roles (fallback):</span>
            {stage?.roleKeys?.map(rk => (
              <span key={rk} className="text-xs font-mono bg-muted px-2 py-0.5 rounded-lg text-muted-foreground border border-border">
                {rk}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Recent Notification Log ──────────────────────────────────────────────────

const RecentNotificationLog = ({ logs, loading }) => {
  const STAGE_META = {
    'Pending Team Assignment': { icon: 'UserPlus', color: 'text-warning', bg: 'bg-warning/10' },
    'Calculated': { icon: 'Calculator', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    'Pending Approval': { icon: 'ClipboardCheck', color: 'text-orange-400', bg: 'bg-orange-500/10' },
    'Approved': { icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
    'Ready for Payment': { icon: 'Banknote', color: 'text-purple-400', bg: 'bg-purple-500/10' },
    'Paid': { icon: 'Receipt', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    'Rejected': { icon: 'XCircle', color: 'text-error', bg: 'bg-error/10' },
    'On Hold': { icon: 'PauseCircle', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!logs?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center">
          <Icon name="BellOff" size={20} className="text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No notifications sent yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {logs?.map(log => {
        const meta = STAGE_META?.[log?.stage] || { icon: 'Bell', color: 'text-primary', bg: 'bg-primary/10' };
        return (
          <div key={log?.id} className="flex items-start gap-3 p-3 rounded-xl bg-card border border-border hover:border-primary/20 transition-colors">
            <div className={`flex-shrink-0 p-2 rounded-lg ${meta.bg}`}>
              <Icon name={meta.icon} size={14} className={meta.color} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {log?.ticket_number && (
                  <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-lg">
                    {log?.ticket_number}
                  </span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>{log?.stage}</span>
                {!log?.is_read && (
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" title="Unread" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{log?.message}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">{timeAgo(log?.created_at)} · {formatDate(log?.created_at)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const WorkflowNotificationPanel = () => {
  const [expandedStage, setExpandedStage] = useState('Pending Team Assignment');
  const [stageUsers, setStageUsers] = useState({});         // role-based users per stage
  const [stageSpecificUsers, setStageSpecificUsers] = useState({}); // specifically assigned users per stage
  const [allSystemUsers, setAllSystemUsers] = useState([]); // all users for the assignment modal
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [activeView, setActiveView] = useState('stages'); // 'stages' | 'log'
  const [assignModalStage, setAssignModalStage] = useState(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);

  // ── Fetch all system users (for assignment modal) ──────────────────────────
  const loadAllSystemUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.eq('is_active', true)
        ?.order('full_name', { ascending: true });

      if (error) throw error;
      setAllSystemUsers(data || []);
    } catch (err) {
      console.warn('[WorkflowNotificationPanel] loadAllSystemUsers error:', err?.message);
    }
  }, []);

  // ── Fetch users per stage (role-based) ────────────────────────────────────
  const loadStageUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          user_id,
          roles!inner(role_key, role_name),
          user_profiles!user_role_assignments_user_id_fkey(id, full_name, email, is_active)
        `);

      if (error) throw error;

      const roleUserMap = {};
      (data || [])?.forEach(row => {
        const rk = row?.roles?.role_key;
        if (!rk) return;
        if (!roleUserMap?.[rk]) roleUserMap[rk] = [];
        const profile = row?.user_profiles;
        if (profile && profile?.is_active !== false) {
          roleUserMap?.[rk]?.push({
            id: profile?.id,
            full_name: profile?.full_name,
            email: profile?.email,
            role_key: rk,
            role_name: row?.roles?.role_name,
          });
        }
      });

      const result = {};
      WORKFLOW_STAGES?.forEach(stage => {
        const seen = new Set();
        const users = [];
        stage?.roleKeys?.forEach(rk => {
          (roleUserMap?.[rk] || [])?.forEach(u => {
            if (!seen?.has(u?.id)) {
              seen?.add(u?.id);
              users?.push(u);
            }
          });
        });
        result[stage.id] = users;
      });

      setStageUsers(result);
    } catch (err) {
      console.warn('[WorkflowNotificationPanel] loadStageUsers error:', err?.message);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // ── Fetch specific user assignments ───────────────────────────────────────
  const loadSpecificAssignments = useCallback(async () => {
    try {
      const map = await fetchAllStageAssignments();
      setStageSpecificUsers(map || {});
    } catch (err) {
      console.warn('[WorkflowNotificationPanel] loadSpecificAssignments error:', err?.message);
    }
  }, []);

  // ── Fetch recent notification logs ────────────────────────────────────────
  const loadRecentLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('incentive_notifications')
        ?.select('id, ticket_number, project_name, stage, message, is_read, created_at')
        ?.order('created_at', { ascending: false })
        ?.limit(30);

      if (error) throw error;
      setRecentLogs(data || []);
    } catch (err) {
      console.warn('[WorkflowNotificationPanel] loadRecentLogs error:', err?.message);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStageUsers();
    loadRecentLogs();
    loadSpecificAssignments();
    loadAllSystemUsers();
  }, [loadStageUsers, loadRecentLogs, loadSpecificAssignments, loadAllSystemUsers]);

  // ── Handle save assignment ─────────────────────────────────────────────────
  const handleSaveAssignment = async (stageId, userIds) => {
    setSavingAssignment(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const success = await saveStageUserAssignments(stageId, userIds, user?.id);
      if (success) {
        // Refresh specific assignments
        await loadSpecificAssignments();
        setSaveSuccess(stageId);
        setTimeout(() => setSaveSuccess(null), 3000);
        setAssignModalStage(null);
      }
    } catch (err) {
      console.warn('[WorkflowNotificationPanel] handleSaveAssignment error:', err?.message);
    } finally {
      setSavingAssignment(false);
    }
  };

  const totalSpecificAssigned = Object.values(stageSpecificUsers)?.reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const totalRoleAssigned = Object.values(stageUsers)?.reduce((sum, arr) => sum + (arr?.length || 0), 0);
  const unreadCount = recentLogs?.filter(l => !l?.is_read)?.length;

  return (
    <div className="space-y-6">
      {/* ── Summary Stats ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="GitBranch" size={14} className="text-primary" />
            <span className="text-xs text-muted-foreground">Workflow Stages</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{WORKFLOW_STAGES?.length}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="UserCheck" size={14} className="text-primary" />
            <span className="text-xs text-muted-foreground">Specific Assignments</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{loadingUsers ? '…' : totalSpecificAssigned}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Bell" size={14} className="text-warning" />
            <span className="text-xs text-muted-foreground">Notifications Sent</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{logsLoading ? '…' : recentLogs?.length}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="BellDot" size={14} className="text-error" />
            <span className="text-xs text-muted-foreground">Unread</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{logsLoading ? '…' : unreadCount}</p>
        </div>
      </div>

      {/* ── Save success toast ────────────────────────────────────────────── */}
      {saveSuccess && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-success/10 border border-success/30">
          <Icon name="CheckCircle" size={16} className="text-success" />
          <p className="text-sm text-success font-medium">
            User assignment saved for <span className="font-bold">{saveSuccess}</span> stage. Changes are live.
          </p>
        </div>
      )}

      {/* ── View Toggle ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { id: 'stages', label: 'Stage Configuration', icon: 'GitBranch' },
          { id: 'log',    label: 'Recent Notifications', icon: 'Bell' },
        ]?.map(v => (
          <button
            key={v?.id}
            onClick={() => setActiveView(v?.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all ${
              activeView === v?.id
                ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={v?.icon} size={14} />
            {v?.label}
            {v?.id === 'log' && unreadCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-error/15 text-error text-xs font-bold">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
        <div className="ml-auto pb-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { loadStageUsers(); loadRecentLogs(); loadSpecificAssignments(); }}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Stage Configuration View ──────────────────────────────────────── */}
      {activeView === 'stages' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20">
            <Icon name="Info" size={14} className="text-primary flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Assign specific users to each stage for direct notifications. If no specific users are assigned,
              the system falls back to role-based notification automatically.
            </p>
          </div>

          {WORKFLOW_STAGES?.map((stage, idx) => (
            <div key={stage?.id} className="flex gap-3">
              {/* Step connector */}
              <div className="flex flex-col items-center flex-shrink-0 pt-4">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${stage?.bg} ${stage?.color} border ${stage?.border}`}>
                  {idx + 1}
                </div>
                {idx < WORKFLOW_STAGES?.length - 1 && (
                  <div className="w-px flex-1 bg-border mt-1 min-h-[16px]" />
                )}
              </div>

              {/* Card */}
              <div className="flex-1 min-w-0 pb-2">
                <StageCard
                  stage={stage}
                  roleUsers={loadingUsers ? [] : (stageUsers?.[stage?.id] || [])}
                  specificUsers={stageSpecificUsers?.[stage?.id] || []}
                  isExpanded={expandedStage === stage?.id}
                  onToggle={() => setExpandedStage(prev => prev === stage?.id ? null : stage?.id)}
                  onAssignUsers={(s) => setAssignModalStage(s)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Recent Notifications Log ──────────────────────────────────────── */}
      {activeView === 'log' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing last <span className="font-semibold text-foreground">30</span> notifications across all users
            </p>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-error/15 text-error text-xs font-semibold">
                {unreadCount} unread
              </span>
            )}
          </div>
          <RecentNotificationLog logs={recentLogs} loading={logsLoading} />
        </div>
      )}

      {/* ── User Assignment Modal ─────────────────────────────────────────── */}
      {assignModalStage && (
        <UserAssignmentModal
          stage={assignModalStage}
          allUsers={allSystemUsers}
          currentAssignedIds={(stageSpecificUsers?.[assignModalStage?.id] || [])?.map(u => u?.id)}
          onSave={handleSaveAssignment}
          onClose={() => setAssignModalStage(null)}
          saving={savingAssignment}
        />
      )}
    </div>
  );
};

export default WorkflowNotificationPanel;
